import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-shell-nav-item-css.js';
import { classMap } from 'lit/directives/class-map.js';
import { cache } from 'lit/directives/cache.js';
import { ZuiShellBase } from './zui-shell-base';
import type { PropertyValues } from 'lit';

/**
 * Child element of `<zui-shell-nav>` that renders into a link. Top-level menu items must be accompanied by an icon. Children menu items do not need an icon.
 *
 * @element zui-shell-nav-item
 *
 * @slot - Default, unnamed slot; for inserting text, `<a>`, and `<zui-icon>` elements into `<zui-shell-nav-item>` as top-level menu items and, if applicable, their subnavs
 * @slot icon - Required slot for top-level menu item's icon placement
 * @slot subnav - Slot for children menu-items that is automatically set when there are more than one anchor tag; consumers do not have to assign elements to this slot
 *
 * @cssprop [--zui-shell-nav-active-color=var(--zui-shell-primary-theme, var(--zui-blue))] - Text color of an active `<zui-shell-nav-item>` when you are currently in that section or page
 * @cssprop [--zui-shell-nav-item-hover-color] - Hover color for `<zui-shell-nav-item>`
 *
 * @event navitemclick - Custom event fired when `<zui-shell-nav-item>` is clicked
 */
export class ZuiShellNavItem extends ZuiShellBase {
  /**
   * This property becomes `true` when `<zui-shell-nav-item>` has children items and is expanded. All parent menu items are collapsed by default
   */
  @property({ type: Boolean, reflect: true }) expanded = false;

  /**
   * This property becomes `true` when there are more than one anchor tag in `<zui-shell-nav-item>`
   */
  @property({ type: Boolean }) subnav = false;

  @query('.nav-item') _navItem: HTMLElement;

  @query('#fakeLink') _fakeLink: HTMLAnchorElement;
  /**
   * @ignore TODO: remove `open` property, likely doesn't do anything, look for other uses and remove too
   */
  open = false;
  private _isActive = false;
  private _isSubNav = false;
  private _primaryAnchorTag: HTMLAnchorElement;
  private _primaryAnchorTagObserver: MutationObserver;
  private _activeNavItemObserver: MutationObserver;

  static get styles() {
    const result = [super.styles, style];

    return result;
  }

  constructor() {
    super();

    this._observeNavCollapseChange = true;
  }

  connectedCallback() {
    super.connectedCallback();

    this._isActive = !!this.querySelector('[active]');

    const anchorTags = this.querySelectorAll('a');

    this._isSubNav = this.subnav ? this.subnav : anchorTags.length > 1;

    this._primaryAnchorTag = anchorTags[0];

    if (this._isSubNav) {
      for (let i = 0; i < anchorTags.length; i++) {
        anchorTags[i].setAttribute('slot', 'subnav');
      }
    }

    if (this._primaryAnchorTag) {
      this._setupPrimaryAnchorMutationObserver();
    }
    this._setupActiveNavItemMutationObserver();
  }

  updated(changedProps: PropertyValues) {
    super.updated(changedProps);

    if (changedProps.has('expanded')) {
      this._modifySubnavHeight();
    }
  }

  protected onScreenBreakpointChange() {
    super.onScreenBreakpointChange();
    this._modifySubnavHeight();
  }

  protected onNavCollapseChange() {
    super.onNavCollapseChange();
    this._modifySubnavHeight();
  }

  render() {
    const navIcon = html`
      <div class="icon ${classMap({ active: this._isActive, 'active-background': this._isActive && !this._isSubNav })}"
        ><slot name="icon"><zui-icon class="placeholder" icon="zui-nav-generic-placeholder"></zui-icon></slot>
      </div>
    `;
    const fakeLinkForIcon = html`
      <a class="fake-link-for-icon" href="${this._primaryAnchorTag ? this._primaryAnchorTag.href : '#'}">
        ${navIcon}
      </a>
    `;

    return html`
      <div class="nav-container ${classMap({ collapsed: this._isNavCollapsed && !this._isMobile })}">
        <div class="nav-item" @click="${this.onNavItemClick}">
          ${cache(
            this._isNavCollapsed && !this._isMobile
              ? html` ${this._isSubNav ? navIcon : fakeLinkForIcon} `
              : html`
                  ${this._isSubNav
                    ? html`
                        ${navIcon}<div class="nav-parent-item"><slot></slot></div
                        ><zui-icon class="expand-icon" icon="zui-chevron-down"></zui-icon>
                      `
                    : html`
                        <a id="fakeLink" class="fake-link-for-icon" href="${this._primaryAnchorTag.href}">${navIcon}</a
                        ><slot></slot>
                      `}
                `
          )}
        </div>

        <div class="subnav" @click="${this.onNavItemClick}">
          <div class="subnav--height">
            ${cache(
              this._isNavCollapsed && !this._isMobile
                ? html`
                    ${this._isSubNav
                      ? html` <div class="subnav-parent-item"><slot></slot></div><slot name="subnav"></slot> `
                      : html` <div class="subnav-only-item"><slot></slot></div><slot name="subnav"></slot> `}
                  `
                : html` <slot name="subnav"></slot> `
            )}
          </div>
        </div></div
      >
    `;
  }

  private _modifySubnavHeight() {
    // TODO(pat): this method is due a refactor to clean up (e.g. remove this.shadowRoot calls, etc.)
    // we set subnav to be an empty to prevent "cannot set property 'height' of undefined"
    const subnav = (this.shadowRoot.querySelector('.subnav') as HTMLElement) || { style: { height: {} } };
    const navItemHeight = 50;
    if (!this._isSubNav) {
      return;
    }

    if (this.expanded) {
      let subnavHeight = (this.shadowRoot.querySelector('.subnav--height') as HTMLElement).offsetHeight;
      if (this._isNavCollapsed && this._isMobile) {
        subnavHeight = subnavHeight - navItemHeight;
      }
      subnav.style.height = subnavHeight + 'px';
    } else {
      if (this._isNavCollapsed && !this._isMobile) {
        subnav.style.height = 'auto';
      } else {
        subnav.style.height = '0';
      }
    }
  }

  private onNavItemClick(e: Event) {
    const emitNavItemClick = () => this.dispatchEvent(new CustomEvent('navitemclick', { bubbles: true }));
    if (e.currentTarget === this._navItem) {
      if (this._isSubNav) {
        this.toggleExpanded();
      } else {
        emitNavItemClick();
      }
    } else if ((e.target as HTMLElement).getAttribute('slot') === 'subnav') {
      emitNavItemClick();
    } else if (e.currentTarget === this._fakeLink) {
      emitNavItemClick();
    }
  }

  /**
   * Toggle expanded state
   */
  toggleExpanded(): void {
    if (this._isSubNav) {
      this.expanded = !this.expanded;
    }
  }

  private _setupPrimaryAnchorMutationObserver() {
    this._primaryAnchorTagObserver = new MutationObserver((mutationsList) => {
      for (const m of mutationsList) {
        if (m.type === 'attributes') {
          if (m.attributeName === 'href') {
            this.requestUpdate();
          }
        }
      }
    });

    this._primaryAnchorTagObserver.observe(this._primaryAnchorTag, { attributes: true, attributeFilter: ['href'] });
  }

  private _setupActiveNavItemMutationObserver() {
    this._activeNavItemObserver = new MutationObserver((mutationsList) => {
      for (const m of mutationsList) {
        if (m.type === 'attributes') {
          if (m.attributeName === 'active') {
            this._isActive = !!this.querySelector('[active]');
            this.requestUpdate();
          }
        }
      }
    });
    const anchorTags = this.querySelectorAll('a');
    if (anchorTags) {
      anchorTags.forEach((anchor) =>
        this._activeNavItemObserver.observe(anchor, {
          attributes: true,
          attributeFilter: ['active'],
        })
      );
    }
  }
}

window.customElements.define('zui-shell-nav-item', ZuiShellNavItem);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-nav-item': ZuiShellNavItem;
  }
}
